import { message } from 'antd';
import {
    pvcListCodeSets,
    pvcResolveEnrollment
} from './pvcHelper';

/**
 * PVC gate helpers — discount apply engines stay unchanged; these only decide
 * whether a code is required / valid for a single discount id.
 */

/** Load PVC-gated discount ids when item / global discount modal opens. */
export async function loadGatedIdsForModal({
    posConfig,
    setUserData,
    setState,
    modalOpened
}) {
    setState({ gatedReady: false, gatedIds: new Set() });

    let enrolled = posConfig.has_physical_voucher;
    if (enrolled !== true && enrolled !== false) {
        enrolled = await pvcResolveEnrollment(posConfig.business_id);
        if (enrolled !== null) {
            setUserData({ ...posConfig, has_physical_voucher: enrolled });
        }
    }

    if (enrolled !== true) {
        setState({ gatedReady: true, gatedIds: new Set() });
        return;
    }

    try {
        const data = await pvcListCodeSets(posConfig.business_id);
        setState({
            gatedReady: true,
            gatedIds: gatedDiscountIdsFromCodeSets(data)
        });
    } catch {
        setState({ gatedReady: false, gatedIds: new Set() });
        if (modalOpened) message.error("Gagal memuat physical voucher. Promo tidak bisa diterapkan. Tutup dan buka ulang modal.");
    }
}

/**
 * @param {{ code_sets?: Array<{ pos_item_discount_ids?: number[] }> }} listPayload
 * @returns {Set<number>}
 */
export function gatedDiscountIdsFromCodeSets(listPayload) {
    const gated = new Set();
    const sets = (listPayload && listPayload.code_sets) || [];
    sets.forEach(set => {
        const ids = Array.isArray(set.pos_item_discount_ids)
            ? set.pos_item_discount_ids
            : [];
        ids.forEach(id => {
            const n = Number(id);
            if (n > 0) gated.add(n);
        });
    });
    return gated;
}

/**
 * Skip re-prompt when the same discount is already unlocked for this scope.
 * @param {Array} codes
 * @param {number|string} posItemDiscountId
 * @param {string|null} localTrxDetailId null = global
 */
export function alreadyUnlockedForScope(codes, posItemDiscountId, localTrxDetailId) {
    const id = Number(posItemDiscountId);
    return (codes || []).some(row => {
        if (Number(row.pos_item_discount_id) !== id) return false;
        const rowDetail = row.local_trx_detail_id == null ? null : row.local_trx_detail_id;
        const want = localTrxDetailId == null ? null : localTrxDetailId;
        return rowDetail === want;
    });
}

/**
 * Replace or clear the unlock for one scope (global = local_trx_detail_id null).
 * @param {Array} codes
 * @param {string|null} localTrxDetailId
 * @param {{ code: string, pos_item_discount_id: number|string, local_trx_detail_id: string|null }|null} row
 *   null = clear that scope; otherwise replace with this row
 */
export function setScopeCode(codes, localTrxDetailId, row) {
    const want = localTrxDetailId == null ? null : localTrxDetailId;
    const next = (codes || []).filter(r => {
        const rowDetail = r.local_trx_detail_id == null ? null : r.local_trx_detail_id;
        return rowDetail !== want;
    });
    if (row) {
        next.push({
            code: String(row.code).toUpperCase(),
            pos_item_discount_id: Number(row.pos_item_discount_id),
            local_trx_detail_id:
                row.local_trx_detail_id == null ? null : row.local_trx_detail_id
        });
    }
    return next;
}

/**
 * Lookup must be available AND contain this discount id.
 * @returns {{ ok: true, code: string } | { ok: false, error: string }}
 */
export function validateLookupForDiscount(lookupRow, posItemDiscountId, trxCodes) {
    if (!lookupRow || lookupRow.status !== 'available') {
        if (lookupRow && lookupRow.status === 'used') {
            return { ok: false, error: 'Kode sudah digunakan.' };
        }
        return { ok: false, error: 'Kode tidak tersedia.' };
    }
    const linkedIds = Array.isArray(lookupRow.pos_item_discount_ids)
        ? lookupRow.pos_item_discount_ids
        : [];
    const id = Number(posItemDiscountId);
    if (!linkedIds.some(x => Number(x) === id)) {
        return { ok: false, error: 'Kode tidak berlaku untuk promo ini.' };
    }
    const code = String(lookupRow.code || '')
        .trim()
        .toUpperCase();
    if (
        code &&
        (trxCodes || []).some(
            row => String(row.code || '').toUpperCase() === code
        )
    ) {
        return { ok: false, error: 'Kode sudah dipakai di transaksi ini.' };
    }
    return { ok: true, code };
}
